import type { ScoreItem } from '@cribbage/engine';
import { kindLook, type FaceKind, type FaceLook, type SeatFace } from '~/session/faceOrder.js';
import { avatarSrc } from '~/store/avatars.js';

/**
 * The seat marks: the drawings that go inside an `Avatar`.
 *
 * Graduated out of `dev/sketches/cross-grid-band.tsx` on 2026-09-04, when the
 * Arcade Cross table was built. The drawings are the sketch's, unchanged in
 * shape; what changed is that every hex literal became a token, because a
 * copied value stops following `[data-theme]` the moment one moves and these
 * are now on a player's screen rather than on a workshop page.
 *
 * | Sketch value | Here |
 * |---|---|
 * | `hue` `#CDE6E0` (you) | `--teal-quiet` |
 * | `hue` `#F3D9C6` (a person opposite) | `--tangerine-quiet` |
 * | `hue` `#E4E4DA` (a bot) | `--ground-dark` |
 * | `GuestSlot`'s `#F4F4EC` | `--ground-dark` |
 * | `stroke-gray-500` on the empty ring | `stroke-gray-400`, the disabled stop |
 *
 * `--gray-500` is the stop the palette says has no wearer, so the empty ring
 * takes `--gray-400`, which is the stop for a thing that is deliberately not
 * filled in yet.
 *
 * **A human seat wears a catalog face.** You wear the one you chose; the
 * other people wear the free faces, Fox, Owl, Dog and Cat, that you are not
 * wearing (`session/faceOrder.ts` decides which seat wears which). The art is
 * each face's approved neutral, which ships under `public/avatars/`
 * (`store/avatars.ts`).
 *
 * Every face is drawn in a 44-unit box whatever size it lands at, so a corner
 * measured in points on the container has to be scaled before the plate inside
 * can wear the same one. `plateRadius` is that conversion.
 */

/** The face kinds and looks live with the face order, in `session/faceOrder.ts`. */
export type { FaceKind, FaceLook, SeatFace } from '~/session/faceOrder.js';
export { seatFace } from '~/session/faceOrder.js';

/**
 * Which lines a seat scores, and the key the shared color maps read: `rows`
 * is teal and `columns` tangerine (`Avatar`, `seatWash`, `seatWord`, the
 * plates). A Cross seat carries both readings apart, `side` and `paint`,
 * because its color is not its axis (`CrossSeat.paint`).
 */
export type SeatSide = 'rows' | 'columns';

/**
 * One seat, as every Cross surface needs it.
 *
 * The band, the scoreboard, the count tally, the payout wells and the game
 * over screen all draw the same five facts about a player, so they read one
 * shape rather than five near-copies of it. `name` is the nameplate spelling
 * and carries a bot's difficulty; a sentence about a bot drops it, which
 * `shortName` in the table's own copy does.
 */
export interface CrossSeat extends SeatFace {
  readonly seat: number;
  /** The nameplate spelling: "You", "Bot (Hard)", "Priya". */
  readonly name: string;
  /** The lines the seat scores: what the axis pill, the lane marks' place and the scoresheet say. */
  readonly side: SeatSide;
  /**
   * The color the seat wears, as the key the color maps read: your side is
   * teal and the other side tangerine, whichever axis each owns (BRAND § Teal,
   * tangerine, and periwinkle; Mitch, 2026-10-01). So it follows the viewer,
   * never the axis: `paintOf` in `sides.ts` is the rule.
   */
  readonly paint: SeatSide;
  /** The match so far, in grid margins won. */
  readonly score: number;
  /** This is the seat the viewer is sitting in. */
  readonly you: boolean;
  /**
   * The seat's rating, online. Unset on the offline table, where a bot match
   * is not ranked, and null for a seat that has none. Drawn by the match
   * start panel with the medal in front of it.
   */
  readonly rating?: number | null | undefined;
  /**
   * The dealer's crib, while the game option is on.
   *
   * Unset with the option off, which is every game that ships today, and that
   * is what every crib drawing on the table gates on: a seat with no `crib` is
   * the seat the Arcade already drew. The same object is built for both seats
   * from one reading of the view, so the two rows cannot disagree about how
   * full the crib is or whose it is.
   */
  readonly crib?: SeatCrib | undefined;
  /**
   * Cards this seat still has to place this grid, at a table whose shares are
   * uneven: three seats, where the player alone places twice as often as each
   * teammate (`placementsLeft`). Unset at two and four, where every share is
   * level and the count would say nothing.
   */
  readonly left?: number | undefined;
  /**
   * His Heels, on the dealer's seat alone, once the Cut Card is face up and
   * is a Jack: the engine's own item, said as the station's secondary line
   * ("Two for his heels") for the rest of the grid (`cross-heels`). It is a
   * fact about the seat for this grid, like the crib's fill, and not a control.
   */
  readonly heels?: ScoreItem | undefined;
  /**
   * Classic: this seat deals the hand, said in words for the D badge the band
   * hangs on its head. Unset on every Cross surface, where the crib's own
   * `dealer` flag draws the badge with the dots beside it.
   */
  readonly deals?: string | undefined;
  /**
   * Classic: a third seat's color, worn over the side's (three-handed, the
   * third player is periwinkle). Unset on every Cross surface.
   */
  readonly tone?: SeatTone | undefined;
}

/** What one seat knows about the crib this grid. */
export interface SeatCrib {
  /** This seat deals, so the crib is this seat's. Exactly one seat is true. */
  readonly dealer: boolean;
  /** Cards in the crib so far, 0 to 4. The same figure on both seats. */
  readonly count: number;
  /** How many cards this seat owes the crib in a whole grid. */
  readonly quota: number;
  /** Throws this seat still owes: its quota less its throws this grid. */
  readonly owed: number;
  /**
   * This seat is on move and holding a card nobody else may see.
   *
   * True only for the seat opposite, and only while the option is on: the
   * viewer's own drawn card is in the dock where they can read it.
   */
  readonly holding: boolean;
}

interface FaceProps {
  /** The corner of the enclosing head, already converted by `plateRadius`. */
  readonly rx: number;
}

/**
 * The head's corner, converted into the 44-unit box every face is drawn in.
 *
 * A circle is that same rect at 21, which is why every plate below is a rect
 * and none of them is a circle.
 */
export function plateRadius(size: number, radius: number | undefined): number {
  return radius === undefined ? 21 : (radius / size) * 44;
}

/** The plate behind a human face: the seat's own side, one volume down. */
const PLATE: Record<SeatSide, string> = {
  rows: 'fill-teal-quiet',
  columns: 'fill-tangerine-quiet',
};

/**
 * A seat color no Cross side has: Classic's third player, periwinkle (BRAND §
 * Teal, tangerine, and periwinkle; Mitch, 2026-09-18). Cross has two sides and
 * never passes one, so every Cross face draws exactly as it did.
 */
export type SeatTone = 'periwinkle';

/** The plate a toned face wears instead of its side's: the same `-quiet` stop. */
const TONE_PLATE: Record<SeatTone, string> = { periwinkle: 'fill-periwinkle-quiet' };

/**
 * A catalog face on the seat's plate.
 *
 * The plate is drawn in the same 44-unit box as every other face, so its
 * corner follows the head's. The art is cut out on alpha and sits over it,
 * cropped to fill the box, so the side's color shows around the ears, the way
 * the `Shared - Avatars` sketch draws a candidate.
 *
 * The art is an SVG `image` inside the hidden drawing rather than an `img`
 * with an empty alt. It says the same thing to a screen reader, since the
 * seat's name is printed beside every head and an `Avatar` given a name
 * carries it on its own `img` role. It also keeps React from hoisting a
 * preload link for every head into server-rendered markup.
 */
function CatalogFace({
  rx,
  side,
  tone,
  look,
  mirrored,
}: FaceProps & {
  readonly side: SeatSide;
  /** Overrides the side's plate with a third seat's color. */
  readonly tone?: SeatTone | undefined;
  readonly look: FaceLook;
  readonly mirrored: boolean;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 44 44"
      className={mirrored ? 'block size-full -scale-x-100' : 'block size-full'}
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="1"
        y="1"
        width="42"
        height="42"
        rx={rx}
        className={tone === undefined ? PLATE[side] : TONE_PLATE[tone]}
      />
      <image href={avatarSrc(look)} width="44" height="44" preserveAspectRatio="xMidYMid slice" />
    </svg>
  );
}

/**
 * A bot's mark, whatever the seat.
 *
 * `docs/ux/social/seat-peek.md` says a local identity must not pretend an
 * account exists, and a bot has no account to pretend about: no catalog
 * choice, no card back, no letter standing in for a name. It gets one drawn
 * mark on stone, in every state, so a player can tell at a glance that the
 * seat opposite is not a person. The plate is `--ground-dark` rather than a
 * side colour for the same reason: the ring already says which lines it owns.
 */
function BotFace({ rx }: FaceProps): React.JSX.Element {
  return (
    <svg viewBox="0 0 44 44" className="block size-full" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="42" height="42" rx={rx} className="fill-ground-dark" />
      <path d="M22 9v4" className="stroke-ink" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="22" cy="8" r="2.2" className="fill-ink" />
      <rect
        x="11"
        y="14"
        width="22"
        height="17"
        rx="6"
        className="fill-ground-light stroke-ink"
        strokeWidth="2.4"
      />
      <circle cx="17.5" cy="22" r="2.4" className="fill-ink" />
      <circle cx="26.5" cy="22" r="2.4" className="fill-ink" />
    </svg>
  );
}

/**
 * The signed-out player: present, unnamed, and clearly not yet anybody.
 *
 * A player without an account is still a player, so the seat is drawn in full
 * and the ask rides on the mark rather than arriving as a banner.
 */
function GuestFace({ rx }: FaceProps): React.JSX.Element {
  return (
    <svg viewBox="0 0 44 44" className="block size-full" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="42" height="42" rx={rx} className="fill-ground-dark" />
      <circle
        cx="22"
        cy="17.5"
        r="7.5"
        className="fill-ground-light stroke-ink"
        strokeWidth="2.4"
      />
      <path
        d="M9 38a13 10.5 0 0 1 26 0z"
        className="fill-ground-light stroke-ink"
        strokeWidth="2.4"
      />
      <circle
        cx="22"
        cy="22"
        r="14"
        fill="none"
        className="stroke-gray-400"
        strokeWidth="2.2"
        strokeDasharray="4 4"
      />
    </svg>
  );
}

/** The drawing one seat wears, chosen by kind. */
export function SeatDrawing({
  kind,
  side,
  tone,
  look,
  rx,
  mirrored = false,
}: FaceProps & {
  readonly kind: FaceKind;
  readonly side: SeatSide;
  /** A third seat's color, over the side's plate. Bots and guests keep theirs. */
  readonly tone?: SeatTone | undefined;
  /** Which face a person wears. Unset draws the kind's own (`kindLook`). */
  readonly look?: FaceLook | undefined;
  /**
   * Turn a person's face right. The catalog faces are drawn three-quarter
   * left; the bot and guest marks face front and never mirror.
   */
  readonly mirrored?: boolean | undefined;
}): React.JSX.Element {
  if (kind === 'bot') return <BotFace rx={rx} />;
  if (kind === 'guest') return <GuestFace rx={rx} />;
  return (
    <CatalogFace
      rx={rx}
      side={side}
      tone={tone}
      look={look ?? kindLook(kind)}
      mirrored={mirrored}
    />
  );
}
